/* eslint-disable */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {schema} from '@/features/user/register/stepOne/validations';
import {makeRequired, makeValidate, SelectData} from 'mui-rff';
import {makeYupTranslatedError} from '@/helpers/yup';
import StepOneForm from '@/features/user/register/stepOne/components/form';
import {registerUserStepOne, registerUserStepOneData} from '@/apis/register/stepOne';
import {toastError, toastSuccess} from '@/helpers/toast';
import lGet from 'lodash/get';
import {convertJSONToString, errorMessageFromServer} from '@/utils/server';
import {AxiosResponse} from 'axios';
import {TStepOneForm} from '@/features/user/register/stepOne/types';
import {useTranslation} from 'react-i18next';
import {transformCatalog, concatSelectData} from '@/helpers/generic-transformer';
import {MenuItem} from '@mui/material';
import {removeDecimals, saveDecimals} from "@/helpers/local-storage";
import {useHistory} from "react-router-dom";
import {useDispatch} from "react-redux";
import {setBackdrop} from "@/store/slices/appSlice";
import {checkErrorResponse} from "@/helpers/axios";

const StepOne = ({isModal, closePopover, stepperCallback, from = "Route"}: any): any => {
	//console.log("@StepOne loading component from ", from);

	const [t] = useTranslation();

	const [loading, setLoading] = useState(true);

	// to know if modal is open
	const [modalOpen, setModalOpen] = useState(false);

	const [submitting, setSubmitting] = useState(false);

	const initialValues: any = {
		cat_tax_regimes_id: 1,
		curp: '',
		employer_registration: '',
		cat_payment_methods_id: 2,
		cat_payment_forms_id: 3,
		business_name: '',
		cat_usoscfdi_id: 3,
		customs: 0,
		cat_currencies_id: 1,
		decimals: 2,
	};

	// hack to override values when component mounts
	const initialValuesRef = useRef<any>(initialValues);

	const selectDataTaxRegime: SelectData[] = [{label: t('step_one.helper_tax_regime'), value: '', disabled: true}];
	const selectDataTaxRegimeRef = useRef<SelectData[]>(selectDataTaxRegime);

	const selectDataPaymentForm: SelectData[] = [{label: t('step_one.helper_payment_form'), value: '', disabled: true}];
	const selectDataPaymentFormRef = useRef<SelectData[]>(selectDataPaymentForm);

	const selectDataPaymentMethod: SelectData[] = [{label: t('step_one.helper_payment_method'), value: '', disabled: true}];
	const selectDataPaymentMethodRef = useRef<SelectData[]>(selectDataPaymentMethod);

	const selectDataCFDIUse: SelectData[] = [{label: t('step_one.helper_cfdi_use'), value: '', disabled: true}];
	const selectDataCFDIUseRef = useRef<SelectData[]>(selectDataCFDIUse);

	const selectDataCustoms: SelectData[] = [
		{label: t('step_one.helper_customs'), value: '', disabled: true},
		{label: t('common.no'), value: 0},
		{label: t('common.yes'), value: 1},
	];

	const selectDataCurrencies: SelectData[] = [{label: t('step_one.helper_currency'), value: '', disabled: true}];
	const selectDataCurrenciesRef = useRef<SelectData[]>(selectDataCurrencies);

	const selectDataDecimals: SelectData[] = [
		{label: t('step_one.helper_decimals'), value: '', disabled: true},
		{label: '0', value: 0},
		{label: '1', value: 1},
		{label: '2', value: 2},
		{label: '3', value: 3},
		{label: '4', value: 4},
		{label: '5', value: 5},
		{label: '6', value: 6},
	];

	const validateForm = makeValidate(schema, makeYupTranslatedError);

	const required = makeRequired(schema);

	const getData = useCallback(async () => {
		// set loader
		setLoading(true);

		// call user data promise
		const promiseUserData = registerUserStepOneData();

		try {
			// promise all
			const results = await Promise.all<Promise<AxiosResponse>>([promiseUserData]);

			// set initial values
			let user_data = results[0].data.user_general_data;
			//console.log('user_data', user_data)

			user_data = {
				...user_data,
				cat_tax_regimes_id: user_data?.cat_tax_regimes_id || 1,
				cat_payment_forms_id: user_data?.cat_payment_forms_id || 3,
				cat_payment_methods_id: user_data?.cat_payment_methods_id || 2,
				cat_usoscfdi_id: user_data?.cat_usoscfdi_id || 3,
				customs: (user_data?.customs && user_data.customs > 1) ? 0 : user_data.customs,
			}

			if (user_data.cat_currencies_id) {
				initialValuesRef.current = {...initialValuesRef.current, ...user_data};
			} else {
				initialValuesRef.current = {...initialValuesRef.current, ...user_data, cat_currencies_id: 1};
			}

			// set catalog
			const cfdi_select_data: SelectData[] = transformCatalog(results[0].data.CFDI_use, 'name', 'id');
			selectDataCFDIUseRef.current = concatSelectData(selectDataCFDIUseRef.current, cfdi_select_data);

			// set catalog
			const currencies_select_data: SelectData[] = transformCatalog(results[0].data.currencies, 'name', 'id');
			selectDataCurrenciesRef.current = concatSelectData(selectDataCurrenciesRef.current, currencies_select_data);

			// set catalog
			const payment_form_select_data: SelectData[] = transformCatalog(results[0].data.payment_form, 'descripcion', 'id');
			selectDataPaymentFormRef.current = concatSelectData(selectDataPaymentFormRef.current, payment_form_select_data);

			// set catalog
			const payment_method_select_data: SelectData[] = transformCatalog(results[0].data.payment_method, 'descripcion', 'id');
			selectDataPaymentMethodRef.current = concatSelectData(selectDataPaymentMethodRef.current, payment_method_select_data);

			// set catalog
			const tax_regime_select_data: SelectData[] = transformCatalog(results[0].data.tax_regimes, 'name', 'id');
			selectDataTaxRegimeRef.current = concatSelectData(selectDataTaxRegimeRef.current, tax_regime_select_data);
		} catch (e) {
			checkErrorResponse(e);
		} finally {
			// stop loading
			setLoading(false);
		}
	}, []);

	const history = useHistory();
	const dispatch = useDispatch();
	const submitData = useCallback(async (payload: any) => {
		setSubmitting(true);

		try {
			const response = await registerUserStepOne(payload);
			
			// set decimals current user
			const new_decimals = payload.decimals;
			await removeDecimals();
			await saveDecimals(new_decimals)

			toastSuccess(response.data.message, {
				onOpen: () => (isModal) && dispatch(setBackdrop(true)),
				onClose: () => (isModal) && history.go(0)
			});

			// sense changes in complete register            
			if (!isModal && stepperCallback) {
				stepperCallback();
			}
			
			return true;
		} catch (error) {
			if (lGet(error, 'data')) {
				const errors = error.data.errors;
				const message = errorMessageFromServer(convertJSONToString(errors));
				toastError(message);
			} else {
				console.error('error: ', error);
				checkErrorResponse(error);
			}
			return false;
		} finally {
			setSubmitting(false);
		}
	}, []);

	// yes, this can even be async!
	const onSubmit = async (values: any) => {
		return submitData(values).catch((e) => console.error(e));
	};

	useEffect(() => {
		if (!isModal)
			getData().catch((e) => console.error(e));
	}, []);

	useEffect(() => {
		//console.log("@StepOne sensing value modalOpen: ", modalOpen);
		if (modalOpen) {
			//console.log("@StepOne modal is open and not loaded data first time");
			getData().catch((e: any) => console.error(e));
		}
	}, [modalOpen])

	const formProps: TStepOneForm = {
		onSubmit,
		initialValues: initialValuesRef.current,
		validateForm,
		required,
		submitting,
		selectDataTaxRegime: selectDataTaxRegimeRef.current,
		selectDataPaymentForm: selectDataPaymentFormRef.current,
		selectDataPaymentMethod: selectDataPaymentMethodRef.current,
		selectDataCFDIUse: selectDataCFDIUseRef.current,
		selectDataCustoms,
		selectDataCurrencies: selectDataCurrenciesRef.current,
		selectDataDecimals,
		isModal,
		closePopover,
		setModalOpen,
		loading
	};

	return (
		<>
			{<StepOneForm {...formProps} />}

			{/*{loading && !isModal && <>{t('common.loading')}</>}

			{loading && isModal && <MenuItem>{t('step_one.title')}</MenuItem>}*/}
		</>
	);
};

export default StepOne;
